/**
 * CentroidValidator - Validación de coordenadas usando centroides municipales
 * 
 * Utiliza los centroides calculados desde DERA WFS (límites municipales)
 * para validación geográfica offline sin necesidad de APIs externas.
 * 
 * Sistema: EPSG:25830 (UTM30 ETRS89)
 * @version 1.0.0
 * @date 2026-01-25
 */

import municipiosData from '../data/municipios/municipios_andalucia_CONSOLIDADO.json';

// ============================================================================
// TIPOS
// ============================================================================

export interface Coordinate {
  x: number;
  y: number;
}

export interface MunicipioCentroid {
  codINE: string;
  nombre: string;
  provincia: string;
  centroide: Coordinate;
}

export interface ValidationResult {
  valid: boolean;
  distance: number;       // Distancia en metros al centroide
  centroide: Coordinate;
  reason?: string;        // Razón si no es válido
}

export interface NearestMunicipio {
  codINE: string;
  nombre: string;
  provincia: string;
  distance: number;       // Distancia en metros
  centroide: Coordinate;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Rangos válidos para Andalucía en UTM30 ETRS89 */
const ANDALUCIA_BOUNDS = {
  minX: 100000,
  maxX: 620000,
  minY: 3980000,
  maxY: 4290000
} as const;

/** Distancia máxima por defecto para validación (50km) */
const DEFAULT_MAX_DISTANCE_M = 50000;

// ============================================================================
// CACHE DE CENTROIDES
// ============================================================================

let centroidsCache: Map<string, MunicipioCentroid> | null = null;

/**
 * Inicializa y retorna el cache de centroides
 */
function getCentroidsMap(): Map<string, MunicipioCentroid> {
  if (centroidsCache) return centroidsCache;

  centroidsCache = new Map();
  
  for (const [codINE, muni] of Object.entries(municipiosData.municipios)) {
    const m = muni as {
      cod_ine: string;
      nombre: string;
      provincia: string;
      centroide?: { x: number; y: number };
    };
    
    if (m.centroide) {
      centroidsCache.set(codINE, {
        codINE,
        nombre: m.nombre,
        provincia: m.provincia,
        centroide: { x: m.centroide.x, y: m.centroide.y }
      });
    }
  }

  return centroidsCache;
}

// ============================================================================
// FUNCIONES PÚBLICAS
// ============================================================================

/**
 * Obtiene el centroide de un municipio por código INE
 * @param codINE Código INE de 5 dígitos
 * @returns Coordenada del centroide o null si no existe
 */
export function getCentroidByINE(codINE: string): Coordinate | null {
  const cod = codINE.padStart(5, '0');
  const muni = getCentroidsMap().get(cod);
  return muni?.centroide ?? null;
}

/**
 * Obtiene información completa del municipio por código INE
 * @param codINE Código INE de 5 dígitos
 */
export function getMunicipioByINE(codINE: string): MunicipioCentroid | null {
  const cod = codINE.padStart(5, '0');
  return getCentroidsMap().get(cod) ?? null;
}

/**
 * Calcula la distancia euclidiana entre dos puntos UTM
 * @returns Distancia en metros
 */
export function calculateDistanceUTM(p1: Coordinate, p2: Coordinate): number {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Verifica si una coordenada está dentro de los límites de Andalucía
 */
export function isWithinAndalucia(coord: Coordinate): boolean {
  return (
    coord.x >= ANDALUCIA_BOUNDS.minX &&
    coord.x <= ANDALUCIA_BOUNDS.maxX &&
    coord.y >= ANDALUCIA_BOUNDS.minY &&
    coord.y <= ANDALUCIA_BOUNDS.maxY
  );
}

/**
 * Valida si una coordenada es coherente con el municipio declarado
 * 
 * @param coord Coordenada a validar (UTM30 ETRS89)
 * @param codINE Código INE del municipio declarado
 * @param maxDistanceM Distancia máxima permitida en metros (default: 50000)
 * @returns Resultado de validación con distancia y razón si falla
 * 
 * @example
 * ```ts
 * const result = validateCoordinateForMunicipio(
 *   { x: 450000, y: 4128000 }, 
 *   '18051' // Colomera
 * );
 * // → { valid: true, distance: 1234, centroide: {x:436897, y:4137872} }
 * ```
 */
export function validateCoordinateForMunicipio(
  coord: Coordinate,
  codINE: string,
  maxDistanceM: number = DEFAULT_MAX_DISTANCE_M
): ValidationResult {
  const muni = getMunicipioByINE(codINE);
  
  if (!muni) {
    return {
      valid: false,
      distance: -1,
      centroide: { x: 0, y: 0 },
      reason: `Municipio ${codINE} no encontrado en BD`
    };
  }

  const distance = calculateDistanceUTM(coord, muni.centroide);
  const valid = distance <= maxDistanceM;

  return {
    valid,
    distance: Math.round(distance),
    centroide: muni.centroide,
    reason: valid 
      ? undefined 
      : `Distancia ${Math.round(distance/1000)}km excede máximo ${maxDistanceM/1000}km`
  };
}

/**
 * Encuentra el municipio más cercano a una coordenada
 * 
 * @param coord Coordenada a buscar (UTM30 ETRS89)
 * @returns Municipio más cercano con distancia
 * 
 * @example
 * ```ts
 * const nearest = findNearestMunicipio({ x: 450000, y: 4128000 });
 * // → { codINE: '18051', nombre: 'Colomera', provincia: 'Granada', distance: 1234, ... }
 * ```
 */
export function findNearestMunicipio(coord: Coordinate): NearestMunicipio | null {
  const centroids = getCentroidsMap();
  
  if (centroids.size === 0) return null;

  let nearest: NearestMunicipio | null = null;
  let minDistance = Infinity;

  const values = Array.from(centroids.values());
  for (const muni of values) {
    const distance = calculateDistanceUTM(coord, muni.centroide);
    
    if (distance < minDistance) {
      minDistance = distance;
      nearest = {
        codINE: muni.codINE,
        nombre: muni.nombre,
        provincia: muni.provincia,
        distance: Math.round(distance),
        centroide: muni.centroide
      };
    }
  }

  return nearest;
}

/**
 * Encuentra municipios dentro de un radio dado
 * 
 * @param coord Centro de búsqueda
 * @param radiusM Radio en metros
 * @returns Lista de municipios ordenados por distancia
 */
export function findMunicipiosWithinRadius(
  coord: Coordinate, 
  radiusM: number
): NearestMunicipio[] {
  const results: NearestMunicipio[] = [];
  
  const values = Array.from(getCentroidsMap().values());
  for (const muni of values) {
    const distance = calculateDistanceUTM(coord, muni.centroide);
    
    if (distance <= radiusM) {
      results.push({
        codINE: muni.codINE,
        nombre: muni.nombre,
        provincia: muni.provincia,
        distance: Math.round(distance),
        centroide: muni.centroide
      });
    }
  }

  return results.sort((a, b) => a.distance - b.distance);
}

/**
 * Obtiene todos los centroides disponibles
 */
export function getAllCentroids(): Map<string, MunicipioCentroid> {
  return new Map(getCentroidsMap());
}

/**
 * Obtiene estadísticas de los centroides
 */
export function getCentroidStats(): {
  total: number;
  byProvincia: Record<string, number>;
  bounds: typeof ANDALUCIA_BOUNDS;
} {
  const centroids = getCentroidsMap();
  const byProvincia: Record<string, number> = {};

  const values = Array.from(centroids.values());
  for (const muni of values) {
    byProvincia[muni.provincia] = (byProvincia[muni.provincia] || 0) + 1;
  }

  return {
    total: centroids.size,
    byProvincia,
    bounds: ANDALUCIA_BOUNDS
  };
}

/**
 * Valida una coordenada con múltiples criterios
 * Útil para pipeline de validación completo
 */
export function validateCoordinateFull(
  coord: Coordinate,
  codINE?: string
): {
  withinAndalucia: boolean;
  nearestMunicipio: NearestMunicipio | null;
  matchesDeclared: ValidationResult | null;
  suggestedMunicipio: string | null;
} {
  const withinAndalucia = isWithinAndalucia(coord);
  const nearestMunicipio = findNearestMunicipio(coord);
  
  let matchesDeclared: ValidationResult | null = null;
  let suggestedMunicipio: string | null = null;

  if (codINE) {
    matchesDeclared = validateCoordinateForMunicipio(coord, codINE);
    
    // Si no coincide con el declarado pero hay uno más cercano
    if (!matchesDeclared.valid && nearestMunicipio) {
      if (nearestMunicipio.distance < matchesDeclared.distance || matchesDeclared.distance < 0) {
        suggestedMunicipio = nearestMunicipio.codINE;
      }
    }
  }

  return {
    withinAndalucia,
    nearestMunicipio,
    matchesDeclared,
    suggestedMunicipio
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  getCentroidByINE,
  getMunicipioByINE,
  calculateDistanceUTM,
  isWithinAndalucia,
  validateCoordinateForMunicipio,
  findNearestMunicipio,
  findMunicipiosWithinRadius,
  getAllCentroids,
  getCentroidStats,
  validateCoordinateFull,
  ANDALUCIA_BOUNDS
};
